import type React from "react";

import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { useProfileContext } from "contexts/ProfileContext";
import { View } from "shared/containers/View";
import { MarketTypes } from "types";
import { userIsTherapist } from "utils/profile/profileHelper";

import { AppSettings } from "../components/AppSettings";
import { PatientPreferences } from "../components/PatientPreferences";
import { ProfileBio } from "../components/ProfileBio";
import { ProfileHeader } from "../components/ProfileHeader";
import { ProfileInformation } from "../components/ProfileInformation";
import { ProfilePassword } from "../components/ProfilePassword";
import { ProfileUsLicenses } from "../components/ProfileUsLicenses";
import { WelcomeMessage } from "../components/WelcomeMessage";

export const Settings: React.FC = () => {
  const { t } = useTranslation();
  const { profile } = useProfileContext();
  const isTherapist = userIsTherapist(profile);

  const customWelcomeMessageEnabled: boolean =
    profile?.therapist_profile?.feature_flags?.includes("CUSTOM_WELCOME_MESSAGE") || false;

  return (
    <View title={t("views.profile")} description={t("views.profile_description")}>
      <Container>
        <ProfileHeader />

        <FlexGrid>
          <Col>
            <ProfileInformation />
            {profile?.market !== MarketTypes.SE && <ProfilePassword />}

            {isTherapist && (
              <WelcomeMessageWrapper>
                <WelcomeMessage customWelcomeMessageEnabled={customWelcomeMessageEnabled} />
              </WelcomeMessageWrapper>
            )}
          </Col>

          <Col>
            {isTherapist && (
              <>
                <ProfileBio />
                {profile?.market === MarketTypes.US && <ProfileUsLicenses />}
                <PatientPreferences />
              </>
            )}
            <AppSettings />
          </Col>
        </FlexGrid>
      </Container>
    </View>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FlexGrid = styled.div`
  display: flex;
  gap: 16px;
  ${({ theme }) => theme.belowBreakpoint} {
    flex-direction: column;
  }
`;

const Col = styled.div`
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  ${({ theme }) => theme.belowBreakpoint} {
    display: contents;
  }
`;

const WelcomeMessageWrapper = styled.div`
  ${({ theme }) => theme.belowBreakpoint} {
    order: 99;
  }
`;
